import type { FunctionComponent } from "react";
import { renderToString } from "react-dom/server";

import * as abstractingBlockchainGasFees from "~/_posts/abstracting-blockchain-gas-fees.mdx";
import * as announcingSupportForArbitrum from "~/_posts/announcing-support-for-arbitrum.mdx";
import * as announcingSupportForBase from "~/_posts/announcing-support-for-base.mdx";
import * as announcingSupportForCantonNetwork from "~/_posts/announcing-support-for-canton-network.mdx";
import * as announcingSupportForCelo from "~/_posts/announcing-support-for-celo.mdx";
import * as announcingSupportForEthereumClassic from "~/_posts/announcing-support-for-ethereum-classic.mdx";
import * as announcingSupportForHedera from "~/_posts/announcing-support-for-hedera.mdx";
import * as announcingSupportForOptimism from "~/_posts/announcing-support-for-optimism.mdx";
import * as announcingSupportForSolana from "~/_posts/announcing-support-for-solana.mdx";
import * as announcingSupportForStellar from "~/_posts/announcing-support-for-stellar.mdx";
import * as announcingSupportForUSDCOnBaseArbitrumCelo from "~/_posts/announcing-support-for-usdc-on-base-arbitrum-celo.mdx";
import * as announcingSupportForUSDP from "~/_posts/announcing-support-for-usdp.mdx";
import * as attestations from "~/_posts/attestations.mdx";
import * as baseBeta from "~/_posts/base-beta.mdx";
import * as bitGoStablecoinSupport from "~/_posts/bitgo-stablecoin-support.mdx";
import * as blockchainSpeedAndCostComparisons from "~/_posts/blockchain-speed-and-cost-comparisons.mdx";
import * as blockchainSpeedCostComparisons from "~/_posts/blockchain-speed-cost-comparisons.mdx";
import * as braleBase from "~/_posts/brale-base.mdx";
import * as braleEcosystem from "~/_posts/brale-ecosystem.mdx";
import * as braleFundraising from "~/_posts/brale-fundraising.mdx";
import * as citizenWallet from "~/_posts/citizen-wallet.mdx";
import * as coinflowRealtimeStablecoinPayouts from "~/_posts/coinflow-realtime-stablecoin-payouts.mdx";
import * as context from "~/_posts/context.mdx";
import * as costsToSend1mOnLeadingBlockchains from "~/_posts/costs-to-send-1m-on-leading-blockchains.mdx";
import * as costsToTransfer1mOnChain from "~/_posts/costs-to-transfer-1m-on-chain.mdx";
import * as demystifyingOnrampsAndOfframps from "~/_posts/demystifying-onramps-and-offramps.mdx";
import * as howToLaunchAStablecoin from "~/_posts/how-to-launch-a-stablecoin.mdx";
import * as introducingBraleBusinessAccounts from "~/_posts/introducing-brale-business-accounts.mdx";
import * as introducingClassicUSD from "~/_posts/introducing-classic-usd.mdx";
import * as introducingMXNe from "~/_posts/introducing-mxne.mdx";
import * as introducingStableCoinSBC from "~/_posts/introducing-stable-coin-sbc.mdx";
import * as introducingStablecoinIssuanceAPI from "~/_posts/introducing-stablecoin-issuance-api.mdx";
import * as introducingTreasury from "~/_posts/introducing-treasury.mdx";
import * as launchingStablecoinsOnAlchemyRollups from "~/_posts/launching-stablecoins-on-alchemy-rollups.mdx";
import * as mintAndRedeemStablecoinsWithACHViaPlaid from "~/_posts/mint-and-redeem-stablecoins-with-ach-via-plaid.mdx";
import * as mpcStablecoinVault from "~/_posts/mpc-stablecoin-vault.mdx";
import * as octoberConferencesForBrale from "~/_posts/october-conferences-for-brale.mdx";
import * as ourApproachToAttestations from "~/_posts/our-approach-to-attestations.mdx";
import * as ourQ3HiringCohort from "~/_posts/our-q3-hiring-cohort.mdx";
import * as rainCards from "~/_posts/rain-cards.mdx";
import * as sendStablecoinsByEmail from "~/_posts/send-stablecoins-by-email.mdx";
import * as stableCoinSBCWhitePaper from "~/_posts/stable-coin-sbc-white-paper.mdx";
import * as stablecoinIssuanceAPI from "~/_posts/stablecoin-issuance-api.mdx";
import * as stablecoinIssuanceCLI from "~/_posts/stablecoin-issuance-cli.mdx";
import * as stablecoinMicropayments from "~/_posts/stablecoin-micropayments.mdx";
import * as stablecoinTokenStandardsOnLeadingBlockchains from "~/_posts/stablecoin-token-standards-on-leading-blockchains.mdx";
import * as theStablecoinOnramp from "~/_posts/the-stablecoin-on-ramp.mdx";
import * as toolsForTesting from "~/_posts/tools-for-testing-bespoke-stablecoins-or-digital-assets.mdx";
import * as treasuryBeta from "~/_posts/treasury-beta.mdx";
import * as privatePaymentBlockchain from "~/_posts/understanding-private-blockchains.mdx";
import * as weAreBrale from "~/_posts/we-are-brale.mdx";
import type { Author } from "~/lib/authors";
import { AUTHORS } from "~/lib/authors";
import type { BlogPost } from "~/lib/constants";

export type Mod = typeof weAreBrale;

const SLUG_TO_MOD: Record<BlogPost, Mod> = {
  "announcing-support-for-hedera": announcingSupportForHedera,
  "launching-stablecoins-on-alchemy-rollups":
    launchingStablecoinsOnAlchemyRollups,
  "october-conferences-for-brale": octoberConferencesForBrale,
  "stablecoin-token-standards-on-leading-blockchains":
    stablecoinTokenStandardsOnLeadingBlockchains,
  "stablecoin-micropayments": stablecoinMicropayments,
  "announcing-support-for-usdc-on-base-arbitrum-celo":
    announcingSupportForUSDCOnBaseArbitrumCelo,
  "introducing-brale-business-accounts": introducingBraleBusinessAccounts,
  "costs-to-send-1m-on-leading-blockchains": costsToSend1mOnLeadingBlockchains,
  "our-q3-hiring-cohort": ourQ3HiringCohort,
  "announcing-support-for-canton-network": announcingSupportForCantonNetwork,
  "mint-and-redeem-stablecoins-with-ach-via-plaid":
    mintAndRedeemStablecoinsWithACHViaPlaid,
  "introducing-mxne": introducingMXNe,
  "introducing-classic-usd": introducingClassicUSD,
  "demystifying-onramps-and-offramps": demystifyingOnrampsAndOfframps,
  "announcing-support-for-usdp": announcingSupportForUSDP,
  "announcing-support-for-ethereum-classic":
    announcingSupportForEthereumClassic,
  "the-stablecoin-on-ramp": theStablecoinOnramp,
  "how-to-launch-a-stablecoin": howToLaunchAStablecoin,
  "bitgo-stablecoin-support": bitGoStablecoinSupport,
  "stablecoin-issuance-cli": stablecoinIssuanceCLI,
  "send-stablecoins-by-email": sendStablecoinsByEmail,
  "stablecoin-issuance-api": stablecoinIssuanceAPI,
  "announcing-support-for-arbitrum": announcingSupportForArbitrum,
  "costs-to-transfer-1m-on-chain": costsToTransfer1mOnChain,
  "coinflow-realtime-stablecoin-payouts": coinflowRealtimeStablecoinPayouts,
  attestations: attestations,
  "introducing-stablecoin-issuance-api": introducingStablecoinIssuanceAPI,
  "mpc-stablecoin-vault": mpcStablecoinVault,
  "rain-cards": rainCards,
  "announcing-support-for-solana": announcingSupportForSolana,
  "citizen-wallet": citizenWallet,
  "brale-ecosystem": braleEcosystem,
  "treasury-beta": treasuryBeta,
  "blockchain-speed-cost-comparisons": blockchainSpeedCostComparisons,
  "announcing-support-for-optimism": announcingSupportForOptimism,
  "announcing-support-for-base": announcingSupportForBase,
  "announcing-support-for-stellar": announcingSupportForStellar,
  "our-approach-to-attestations": ourApproachToAttestations,
  "introducing-treasury": introducingTreasury,
  "announcing-support-for-celo": announcingSupportForCelo,
  "tools-for-testing-bespoke-stablecoins-or-digital-assets": toolsForTesting,
  "base-beta": baseBeta,
  "stable-coin-sbc-white-paper": stableCoinSBCWhitePaper,
  "introducing-stable-coin-sbc": introducingStableCoinSBC,
  "brale-fundraising": braleFundraising,
  "understanding-private-blockchains": privatePaymentBlockchain,
  "abstracting-blockchain-gas-fees": abstractingBlockchainGasFees,
  "brale-base": braleBase,
  "blockchain-speed-and-cost-comparisons": blockchainSpeedAndCostComparisons,
  context,
  "we-are-brale": weAreBrale,
};

export type BlogSlugs = string[];

export type Image = Readonly<{
  alt: string;
  url: string;
  link?: string;
}>;

export type CTA = Readonly<{
  seoRef?: string;
  heading?: string;
  subheading?: string;
  button?: string;
  ctaUrl?: string;
  externalUrl?: boolean;
  className?: string;
}>;

type PostAttrs = Readonly<{
  authors: readonly Author[];
  coverImage: Image;
  date: string;
  description: string;
  excerpt: string;
  ogImage?: Image;
  slug: BlogPost;
  timeOverride?: number;
  title: string;
  discussTwitter?: string;
  discussLinkedIn?: string;
  discussFarcaster?: string;
  callToAction?: CTA;
}>;

export type Post = PostAttrs & Readonly<{ content: string }>;

export type GetSelectedPosts = ReturnType<typeof getSelectedPosts>;

export function getSelectedPosts(slugs: BlogSlugs): Post[] {
  const filteredMods = Object.entries(SLUG_TO_MOD).filter(([slug]) =>
    slugs.includes(slug)
  );

  return filteredMods.map(([, mod]) => postFromMod(mod));
}

export type GetPosts = ReturnType<typeof getPosts>;

export function getPosts(): Post[] {
  return Object.entries(SLUG_TO_MOD).map(([, mod]) => postFromMod(mod));
}

export function getPost(slug: string): Post | undefined {
  const mod = SLUG_TO_MOD[slug.toLowerCase() as BlogPost];
  if (mod == null) return;

  return postFromMod(mod);
}

export function postFromMod(mod: Mod): Post {
  const comp: React.ReactNode = (mod.default as FunctionComponent)({});

  return {
    ...mod.attributes,
    authors: mod.attributes.authors.map((a: string) => AUTHORS[a]),
    content: comp ? renderToString(comp) : "",
    slug: mod.filename.replace(/\.mdx?$/, ""),
  };
}
